import { ChatMessage } from '@koolumbus/shared/data';
import { ChatDrawer as ChatDrawerUI } from '@koolumbus/web/ui';
import { docToChatMessage } from '@koolumbus/web/utils';
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuthCtx } from '../../lib/context/auth.context';
import { fbStore } from '../../lib/services/firebase-client';
import { trpc } from '../../lib/trpc';

interface ChatDrawerProps {
  chatId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ChatDrawer: React.FC<React.PropsWithChildren<ChatDrawerProps>> = ({
  chatId,
  isOpen,
  onClose,
}) => {
  const {
    state: { user },
  } = useAuthCtx();
  const trpcCtx = trpc.useUtils();
  const router = useRouter();

  const { data } = trpc.chats.get.useQuery({ id: chatId }, { enabled: isOpen });
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const onRead = useCallback(async () => {
    try {
      await updateDoc(doc(fbStore, 'chats', chatId), {
        userHasUnreadMessages: false,
      });
    } catch (err) {
      console.error(err);
    }
  }, [chatId]);

  useEffect(() => {
    let unsubscribe = () => {};

    if (isOpen && user?.id) {
      const snapQuery = query(collection(fbStore, 'chats', chatId, 'messages'), orderBy('sentAt'));

      unsubscribe = onSnapshot(snapQuery, (snap) => {
        const items = snap.docs.map(docToChatMessage);
        setMessages(items);

        for (const change of snap.docChanges()) {
          const data = change.doc.data() as { sentBy: string };
          if (change.type === 'added' && data.sentBy !== user?.id) {
            onRead();
            break;
          }
        }
      });
    }

    return () => unsubscribe();
  }, [chatId, isOpen, onRead, user?.id]);

  const { mutateAsync, isLoading: isSendLoading } = trpc.chats.sendMessage.useMutation({
    onSuccess: () => {
      trpcCtx.chats.list.invalidate();
    },
  });

  const onSend = async (text: string) => {
    await mutateAsync({
      chatId,
      text,
    });
  };

  const onOpenProperty = () => {
    router.push(`/property/${data?.booking.property.id}`);
  };

  return (
    <ChatDrawerUI
      isOpen={isOpen}
      onClose={onClose}
      title={data ? data.tenant.name : 'Loading...'}
      isPMS={false}
      chat={data ?? null}
      onSend={onSend}
      isSendLoading={isSendLoading}
      messages={messages}
      onOpenProperty={onOpenProperty}
    />
  );
};

export default ChatDrawer;
